import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { 
  BookOpen, Home, Search, Heart, User, ChevronRight, ChevronLeft,
  Download, Menu, X, Sparkles, Star, PenLine, MessageCircle, 
  Play, Volume2, ArrowUp, Settings
} from 'lucide-react';
import { APP_CONFIG, PDF_URL } from '../constants/appConfig';
import { Input } from './ui/input';
import { toast } from 'sonner';
import FeelingsNeedsHelper from './FeelingsNeedsHelper';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Quick access sections for mobile
const QUICK_SECTIONS = [
  { id: 'feelings', title: 'Feelings & Needs', emoji: '💗', link: '/workbook/feelings-needs-exercise', color: 'from-rose-500 to-pink-600' },
  { id: 'ofnr', title: 'OFNR Practice', emoji: '🦒', link: '/workbook/ofnr-practice-sheets', color: 'from-emerald-500 to-teal-600' },
  { id: 'chapter1', title: 'Chapter 1', emoji: '📖', link: '/workbook/chapter-1', color: 'from-blue-500 to-indigo-600' },
  { id: 'trainer', title: 'OFNR Trainer', emoji: '🎯', link: '/ofnr-trainer', color: 'from-amber-500 to-orange-600' },
];

// Full table of contents
const TABLE_OF_CONTENTS = [
  { section: 'Getting Started', items: [
    { page: 1, title: 'Welcome & Purpose', link: '/workbook/wellness-in-relationships' },
    { page: 5, title: 'Feelings & Needs Exercise', link: '/workbook/feelings-needs-exercise', featured: true },
    { page: 16, title: 'Basic Feelings & Needs', link: '/workbook/basic-feelings-needs' },
    { page: 17, title: 'OFNR Overview', link: '/workbook/ofnr-statements-questions' },
  ]},
  { section: 'Practice Sheets', items: [
    { page: 18, title: 'OFNR Practice Sheets', link: '/workbook/ofnr-practice-sheets', featured: true },
    { page: 25, title: 'Gratitude Exercises', link: '/workbook/gratitude-exercises' },
    { page: 38, title: 'Self-Empathy', link: '/workbook/self-empathy' },
  ]},
  { section: 'Chapter 1', items: [
    { page: 79, title: 'Alienation of Life', link: '/workbook/chapter-1?section=1' },
    { page: 80, title: 'Moralistic Judgments', link: '/workbook/chapter-1?section=2' },
    { page: 82, title: 'Making Comparisons', link: '/workbook/chapter-1?section=3' },
    { page: 84, title: 'Denial of Responsibility', link: '/workbook/chapter-1?section=4' },
    { page: 85, title: 'Jackal to OFNR Practice', link: '/workbook/chapter-1?section=5' },
  ]},
  { section: 'Tools & Resources', items: [
    { page: 0, title: 'OFNR Trainer', link: '/ofnr-trainer' },
    { page: 0, title: 'LORE Library', link: '/lore' },
    { page: 0, title: 'ElchmE Board', link: '/elchme' },
  ]},
];

export default function MobileEbook() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('home');
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isHelperOpen, setIsHelperOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [favorites, setFavorites] = useState(() => {
    const saved = localStorage.getItem('ebookFavorites');
    return saved ? JSON.parse(saved) : [];
  });
  const [recentlyViewed, setRecentlyViewed] = useState(() => {
    const saved = localStorage.getItem('ebookRecent');
    return saved ? JSON.parse(saved) : [];
  });
  const [featuredHighlights, setFeaturedHighlights] = useState([]);
  const contentRef = useRef(null);

  // Fetch featured highlights
  useEffect(() => {
    const fetchHighlights = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/ebook/highlights`);
        if (response.ok) {
          const data = await response.json();
          setFeaturedHighlights(data.filter(h => h.active));
        }
      } catch (error) {
        console.log('Could not fetch highlights');
      }
    };
    fetchHighlights();
  }, []);

  // Scroll handling
  useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        setShowScrollTop(contentRef.current.scrollTop > 300);
      }
    };
    const el = contentRef.current;
    if (el) el.addEventListener('scroll', handleScroll);
    return () => { if (el) el.removeEventListener('scroll', handleScroll); };
  }, []);

  const toggleFavorite = (item) => {
    setFavorites(prev => {
      const exists = prev.find(f => f.link === item.link);
      const updated = exists 
        ? prev.filter(f => f.link !== item.link)
        : [...prev, { ...item, addedAt: Date.now() }];
      localStorage.setItem('ebookFavorites', JSON.stringify(updated));
      toast.success(exists ? 'Removed from favorites' : 'Added to favorites');
      return updated;
    });
  };

  const isFavorite = (link) => favorites.some(f => f.link === link);

  const navigateToPage = (item) => {
    // Add to recently viewed
    setRecentlyViewed(prev => {
      const filtered = prev.filter(r => r.link !== item.link);
      const updated = [{ ...item, viewedAt: Date.now() }, ...filtered].slice(0, 10);
      localStorage.setItem('ebookRecent', JSON.stringify(updated));
      return updated;
    });
    navigate(item.link);
  };

  const scrollToTop = () => {
    contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const downloadPDF = () => {
    const link = document.createElement('a');
    link.href = PDF_URL;
    link.download = APP_CONFIG.workbookPdfFilename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Download started!');
  };

  const filteredContents = TABLE_OF_CONTENTS.map(section => ({
    ...section,
    items: section.items.filter(item => 
      item.title.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(section => section.items.length > 0);

  return (
    <div className="h-screen flex flex-col bg-slate-950 text-white overflow-hidden">
      
      {/* Mobile Header - Fixed */}
      <header className="flex-shrink-0 bg-gradient-to-r from-slate-900 to-slate-800 border-b border-slate-700 safe-area-inset-top">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/')} className="p-2 -ml-2 text-slate-400 hover:text-white">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-white">Belonging eBook</h1>
              <p className="text-xs text-slate-500">The Belonging Sessions</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              className="p-2.5 rounded-full bg-slate-800 text-slate-400 hover:text-white"
            >
              <Search className="w-5 h-5" />
            </button>
            <button 
              onClick={() => setIsHelperOpen(true)}
              className="p-2.5 rounded-full bg-gradient-to-r from-rose-500 to-pink-600 text-white"
            >
              <Heart className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Search Bar - Expandable */}
        {isSearchOpen && (
          <div className="px-4 pb-3 animate-in slide-in-from-top duration-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search topics..."
                className="w-full pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 rounded-xl"
                autoFocus
              />
              {searchTerm && (
                <button 
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Main Content - Scrollable */}
      <main 
        ref={contentRef}
        className="flex-1 overflow-y-auto overscroll-contain"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        
        {/* Home Tab */}
        {activeTab === 'home' && !searchTerm && (
          <div className="pb-24">
            
            {/* Hero Card */}
            <div className="p-4">
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-900 via-teal-900 to-slate-900 p-6">
                <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/20 rounded-full blur-3xl"></div>
                <div className="relative z-10">
                  <p className="text-emerald-400 text-xs font-medium tracking-wider uppercase mb-2">
                    Continue Learning
                  </p>
                  <h2 className="text-2xl font-bold text-white mb-2">
                    Compassionate Communication
                  </h2>
                  <p className="text-slate-300 text-sm mb-4">
                    Master the language of connection through NVC
                  </p>
                  <Button 
                    onClick={() => navigateToPage({ link: '/workbook/feelings-needs-exercise', title: 'Feelings & Needs' })}
                    className="bg-white text-slate-900 hover:bg-slate-100 rounded-xl font-semibold"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Start Exercise
                  </Button>
                </div>
              </div>
            </div>

            {/* Today's Focus - Featured Highlights */}
            {featuredHighlights.length > 0 && (
              <div className="px-4 mb-6">
                <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">
                  Today's Focus
                </h3>
                <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
                  {featuredHighlights.map(highlight => (
                    <button
                      key={highlight._id}
                      onClick={() => navigateToPage({ link: `/workbook/page-${highlight.page}`, title: highlight.title })}
                      className="flex-shrink-0 w-40 p-4 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-600/20 border border-amber-500/30 text-left"
                    >
                      <Sparkles className="w-5 h-5 text-amber-500 mb-2" />
                      <p className="text-white font-medium text-sm line-clamp-2">{highlight.title}</p>
                      <p className="text-slate-500 text-xs mt-1">Page {highlight.page}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Access Grid */}
            <div className="px-4 mb-6">
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">
                Quick Access
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {QUICK_SECTIONS.map(section => (
                  <button
                    key={section.id}
                    onClick={() => navigateToPage({ link: section.link, title: section.title })}
                    className={`p-4 rounded-xl bg-gradient-to-br ${section.color} text-left active:scale-95 transition-transform`}
                  >
                    <span className="text-2xl mb-2 block">{section.emoji}</span>
                    <p className="text-white font-semibold text-sm">{section.title}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Recently Viewed */}
            {recentlyViewed.length > 0 && (
              <div className="px-4 mb-6">
                <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">
                  Recently Viewed
                </h3>
                <div className="space-y-2">
                  {recentlyViewed.slice(0, 5).map((item, i) => (
                    <button
                      key={i}
                      onClick={() => navigateToPage(item)}
                      className="w-full flex items-center gap-3 p-3 rounded-xl bg-slate-800/50 text-left active:bg-slate-700"
                    >
                      <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center">
                        <BookOpen className="w-5 h-5 text-slate-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium text-sm truncate">{item.title}</p>
                        <p className="text-slate-500 text-xs">Tap to continue</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Download PDF Card */}
            <div className="px-4 mb-6">
              <button
                onClick={downloadPDF}
                className="w-full flex items-center gap-4 p-4 rounded-xl bg-slate-800 border border-slate-700 active:bg-slate-700"
              >
                <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center">
                  <Download className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-white font-semibold">Download PDF</p>
                  <p className="text-slate-500 text-sm">Offline reading</p>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-600" />
              </button>
            </div>
          </div>
        )}

        {/* Contents Tab */}
        {(activeTab === 'contents' || searchTerm) && (
          <div className="pb-24">
            <div className="p-4">
              {filteredContents.length === 0 ? (
                <div className="text-center py-12">
                  <Search className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-500">No results found</p>
                </div>
              ) : (
                filteredContents.map((section, sectionIdx) => (
                  <div key={sectionIdx} className="mb-6">
                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 px-1">
                      {section.section}
                    </h3>
                    <div className="space-y-2">
                      {section.items.map((item, itemIdx) => (
                        <div
                          key={itemIdx}
                          className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/50 active:bg-slate-700"
                        >
                          <button
                            onClick={() => navigateToPage(item)}
                            className="flex-1 flex items-center gap-3 text-left"
                          >
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              item.featured ? 'bg-gradient-to-br from-amber-500 to-orange-600' : 'bg-slate-700'
                            }`}>
                              {item.featured ? (
                                <Star className="w-5 h-5 text-white" />
                              ) : (
                                <span className="text-slate-400 text-sm font-medium">
                                  {item.page || '—'}
                                </span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-white font-medium text-sm truncate">{item.title}</p>
                              {item.page > 0 && (
                                <p className="text-slate-500 text-xs">Page {item.page}</p>
                              )}
                            </div>
                          </button>
                          <button 
                            onClick={() => toggleFavorite(item)}
                            className={`p-2 rounded-full ${isFavorite(item.link) ? 'text-rose-500' : 'text-slate-600'}`}
                          >
                            <Heart className={`w-5 h-5 ${isFavorite(item.link) ? 'fill-current' : ''}`} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Favorites Tab */}
        {activeTab === 'favorites' && !searchTerm && (
          <div className="pb-24 p-4">
            {favorites.length === 0 ? (
              <div className="text-center py-16">
                <Heart className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">No Favorites Yet</h3>
                <p className="text-slate-500 text-sm max-w-xs mx-auto">
                  Tap the heart icon on any page to add it to your favorites for quick access.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {favorites.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/50"
                  >
                    <button
                      onClick={() => navigateToPage(item)}
                      className="flex-1 flex items-center gap-3 text-left"
                    >
                      <div className="w-10 h-10 rounded-lg bg-rose-500/20 flex items-center justify-center">
                        <Heart className="w-5 h-5 text-rose-500 fill-current" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium text-sm truncate">{item.title}</p>
                        {item.page > 0 && (
                          <p className="text-slate-500 text-xs">Page {item.page}</p>
                        )}
                      </div>
                    </button>
                    <button 
                      onClick={() => toggleFavorite(item)}
                      className="p-2 rounded-full text-slate-600"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && !searchTerm && (
          <div className="pb-24 p-4">
            <div className="text-center py-8 mb-6">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mx-auto mb-4">
                <User className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-xl font-semibold text-white">Welcome</h2>
              <p className="text-slate-500 text-sm">Your NVC Journey</p>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full flex items-center gap-4 p-4 rounded-xl bg-slate-800 active:bg-slate-700"
              >
                <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-white font-medium">Dashboard</p>
                  <p className="text-slate-500 text-sm">View your progress</p>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-600" />
              </button>

              <button
                onClick={() => navigate('/lore')}
                className="w-full flex items-center gap-4 p-4 rounded-xl bg-slate-800 active:bg-slate-700"
              >
                <div className="w-10 h-10 rounded-lg bg-amber-600 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-white font-medium">LORE Library</p>
                  <p className="text-slate-500 text-sm">Stories & wisdom</p>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-600" />
              </button>

              <button
                onClick={() => navigate('/ofnr-trainer')}
                className="w-full flex items-center gap-4 p-4 rounded-xl bg-slate-800 active:bg-slate-700"
              >
                <div className="w-10 h-10 rounded-lg bg-emerald-600 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-white font-medium">OFNR Trainer</p>
                  <p className="text-slate-500 text-sm">Practice tool</p>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-600" />
              </button>

              <button
                onClick={downloadPDF}
                className="w-full flex items-center gap-4 p-4 rounded-xl bg-slate-800 active:bg-slate-700"
              >
                <div className="w-10 h-10 rounded-lg bg-purple-600 flex items-center justify-center">
                  <Download className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-white font-medium">Download PDF</p>
                  <p className="text-slate-500 text-sm">For offline reading</p>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-600" />
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="absolute bottom-24 right-4 w-12 h-12 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-white shadow-lg animate-in fade-in duration-200"
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      )}

      {/* Bottom Navigation - Fixed */}
      <nav className="flex-shrink-0 bg-slate-900 border-t border-slate-800 safe-area-inset-bottom">
        <div className="flex justify-around items-center py-2">
          {[
            { id: 'home', icon: Home, label: 'Home' },
            { id: 'contents', icon: BookOpen, label: 'Contents' },
            { id: 'favorites', icon: Heart, label: 'Favorites' },
            { id: 'profile', icon: User, label: 'Profile' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setSearchTerm(''); }}
              className={`flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-colors ${
                activeTab === tab.id 
                  ? 'text-emerald-500' 
                  : 'text-slate-500 active:text-slate-300'
              }`}
            >
              <tab.icon className={`w-6 h-6 ${activeTab === tab.id ? 'fill-current' : ''}`} />
              <span className="text-xs font-medium">{tab.label}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Feelings & Needs Helper */}
      <FeelingsNeedsHelper 
        isOpen={isHelperOpen} 
        onClose={() => setIsHelperOpen(false)} 
      />
    </div>
  );
}
